import CustomTooltip from '../../../../shared/CustomTooltip';
import {
    Box,
    FormControl,
    FormHelperText,
    Grid,
    List,
    Typography,
    useTheme,
} from '@mui/material';
import { get } from 'lodash';
import React, { useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';

import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CustomIconButton } from '../../../../../layout/styledcomponents/CustomIconButton';
import { capitalizeName } from '../../../../../shared/utils';
import FormCheckbox from '../../../../shared/FormCheckbox';
import PolicyModal from '../../../Policies/PolicyModal';
import { bookingPoliciesContent } from '../../../Policies/PolicyForm';
import { Policy } from '../../../../types/policy';

interface Props {
    policy?: Policy;
    control: Control<any>;
}

const ReservationPolicy = ({ policy, control }: Props) => {
    const { formState } = useFormContext();
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const checkedError = get(formState.errors, `policy.checked`);

    const fairPolicyTitle = 'Fair';
    const fairPolicyItems = [
        '100% refund if cancelled at least 7 days before check-in/event.',
        '50% refund if cancelled 3–6 days before check-in/event.',
        'No refund if cancelled less than 3 days before check-in/event.',
        'Note: 7DAY retains both host and guests service fees.',
    ];

    const theme = useTheme();

    const cancellationPolicyDetails = bookingPoliciesContent.find(
        (item) => item.value === policy?.cancellationType,
    );

    if (!policy) {
        return (
            <Box>
                <Grid
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant='body1'>
                        Fair cancellation policy
                    </Typography>
                    <CustomTooltip
                        placement='left'
                        title={
                            <Box>
                                <Typography
                                    variant='body1'
                                    sx={{
                                        textDecoration: 'underline',
                                        mb: 0.5,
                                        color: theme.palette.neutral.main,
                                    }}
                                >
                                    {fairPolicyTitle}
                                </Typography>
                                <List
                                    sx={{
                                        pl: 2,
                                        listStyleType: 'disc',
                                    }}
                                >
                                    {fairPolicyItems.map((text, i) => (
                                        <Box key={i} sx={{ mb: 0.25 }}>
                                            <Typography
                                                variant='body1'
                                                sx={{
                                                    color: theme.palette.neutral
                                                        .main,
                                                    display: 'list-item',
                                                }}
                                            >
                                                {text}
                                            </Typography>
                                        </Box>
                                    ))}
                                </List>
                            </Box>
                        }
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: theme.palette.primary.main,
                                    maxWidth: 260,
                                    p: 1.5,
                                    borderRadius: 1.5,
                                },
                            },
                        }}
                    >
                        <InfoOutlinedIcon
                            sx={{
                                fontSize: 16,
                                cursor: 'pointer',
                                flex: '0 0 auto',
                                marginRight: '22px',
                            }}
                        />
                    </CustomTooltip>
                </Grid>
            </Box>
        );
    }

    return (
        <>
            <Grid
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <FormControl>
                    <Box display='flex' alignItems='center' sx={{ m: 0 }}>
                        <FormCheckbox
                            id='policy__checkbox'
                            name={`policy.checked`}
                            control={control}
                            sx={{ p: 0, m: 0 }}
                        />
                        <Typography
                            variant='caption'
                            fontWeight={550}
                            sx={{ ml: 1 }}
                            component='label'
                            htmlFor='policy__checkbox'
                        >
                            {capitalizeName(policy?.name)}
                        </Typography>
                    </Box>
                    {checkedError && (
                        <FormHelperText error sx={{ ml: 0 }}>
                            {checkedError.message as string}
                        </FormHelperText>
                    )}
                </FormControl>
                <CustomIconButton
                    variant='contained'
                    color='primary'
                    onClick={() => setModalOpen(true)}
                >
                    <KeyboardArrowRightIcon />
                </CustomIconButton>
            </Grid>

            {cancellationPolicyDetails && (
                <PolicyModal
                    policy={policy}
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    cancellationPolicyDetails={cancellationPolicyDetails}
                />
            )}
        </>
    );
};

export default ReservationPolicy;
